import { ClassValue } from 'clsx';
import { isNil } from 'lodash';
import { memo } from 'react';
import { Field } from '@shared/data/entity/fields/Field';
import { TimeTrackerField } from '@shared/data/entity/fields/TimeTrackerField';
import { isInputButtonComponent, isTimeTrackerComponent } from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { CollectionViewAnalytics } from '../../CollectionViewAnalytics';
import { InputButton } from '../staticComponents/InputButton';
import { AddressCollectionViewField } from './AddressCollectionViewField';
import { CombinedTagsCollectionViewField } from './CombinedTagsCollectionViewField';
import { DateCollectionViewField } from './DateCollectionViewField';
import { DateRangeCollectionViewField } from './DateRangeCollectionViewField';
import { EmailCollectionViewField } from './EmailCollectionViewField';
import { EntityBatchCollectionViewField } from './EntityBatchCollectionViewField';
import { FieldVariant } from './FieldVariant';
import { FileCollectionViewField } from './FileCollectionViewField';
import { GeolocationCollectionViewField } from './GeolocationCollectionViewField';
import { NumberCollectionViewField } from './NumberCollectionViewField';
import { PersonCollectionViewField } from './PersonCollectionViewField';
import { RelatedCardCollectionViewField } from './RelatedCardCollectionViewField';
import { RelatedLookupCollectionViewField } from './RelatedLookupCollectionViewField';
import { ScheduledAutomationCollectionViewField } from './ScheduledAutomationCollectionViewField';
import { SignatureCollectionViewField } from './SignatureCollectionViewField';
import { SubformCollectionViewField } from './SubformCollectionViewField';
import { TagCollectionViewField } from './TagCollectionViewField';
import { TextCollectionViewField } from './TextCollectionViewField';
import { TimeTrackerCollectionViewField } from './TimeTrackerCollectionViewField';
import { TodoCollectionViewField } from './TodoCollectionViewField';
import { UnknownCollectionViewField } from './UnknownCollectionViewField';
import { UrlCollectionViewField } from './UrlCollectionViewField';

interface Props {
  /** None for a field with no value, and always none for an input button. */
  field?: Field | null;
  component: API.AnyComponent;
  variant?: FieldVariant;
  className?: ClassValue;
  workflowTemplateId: string;
  entityId: string;
  /** Shows the controls that change the card: an input button, a timer's start/stop. */
  canUpdateCard: boolean;
  analytics?: CollectionViewAnalytics;
  /** The host renders the field's label above it; an input button makes room for it. */
  showLabel?: boolean;
  /** Timers only: the stopwatch beside the time, as tables and subtables show it. */
  showTimerIcon?: boolean;
}

/**
 * A field's value in the variant the host asks for: the one place that picks a renderer by field
 * type. Every place that shows a field goes through here: collection view tables, list and board
 * cards, calendar cards, and subtables. Dispatches on the component first, since an input button,
 * and a timer that has never run, have no value.
 */
export const CollectionViewField = memo(function CollectionViewFieldComponent({
  field,
  component,
  variant = 'default',
  className,
  workflowTemplateId,
  entityId,
  canUpdateCard,
  analytics,
  showLabel,
  showTimerIcon,
}: Readonly<Props>) {
  if (isInputButtonComponent(component)) {
    return (
      <InputButton
        component={component}
        workflowTemplateId={workflowTemplateId}
        entityId={entityId}
        variant={variant}
        className={className}
        withLabel={showLabel}
        canUpdateCard={canUpdateCard}
        analytics={analytics}
      />
    );
  }

  // A timer that has never run has no value, but still offers its Start button.
  if (isTimeTrackerComponent(component)) {
    return (
      <TimeTrackerCollectionViewField
        variant={variant}
        className={className}
        component={component}
        entityId={entityId}
        field={field instanceof TimeTrackerField ? field : null}
        canUpdateCard={canUpdateCard}
        showIcon={showTimerIcon}
      />
    );
  }

  if (isNil(field)) {
    return null;
  }

  switch (field.fieldType) {
    case 'AddressField':
      return <AddressCollectionViewField field={field} variant={variant} className={className} />;
    case 'CombinedTagsField':
      return (
        <CombinedTagsCollectionViewField field={field} variant={variant} className={className} />
      );
    case 'DateField':
      return <DateCollectionViewField field={field} variant={variant} className={className} />;
    case 'DateRangeField':
      return <DateRangeCollectionViewField field={field} variant={variant} className={className} />;
    case 'EmailField':
      return <EmailCollectionViewField field={field} variant={variant} className={className} />;
    case 'EntityBatch':
      return (
        <EntityBatchCollectionViewField field={field} variant={variant} className={className} />
      );
    case 'FileField':
      return <FileCollectionViewField field={field} variant={variant} className={className} />;
    case 'GeolocationField':
      return (
        <GeolocationCollectionViewField field={field} variant={variant} className={className} />
      );
    case 'NumberField':
      return <NumberCollectionViewField field={field} variant={variant} className={className} />;
    case 'PersonField':
      return <PersonCollectionViewField field={field} variant={variant} className={className} />;
    case 'RelatedEntityField':
      return (
        <RelatedCardCollectionViewField
          field={field}
          variant={variant}
          className={className}
          component={component}
        />
      );
    case 'RelatedLookupField':
      return (
        <RelatedLookupCollectionViewField
          field={field}
          variant={variant}
          className={className}
          component={component}
          workflowTemplateId={workflowTemplateId}
        />
      );
    case 'ScheduledAutomationField':
      return (
        <ScheduledAutomationCollectionViewField
          field={field}
          variant={variant}
          className={className}
        />
      );
    case 'SignatureField':
      return <SignatureCollectionViewField field={field} variant={variant} className={className} />;
    case 'SubformField':
      return <SubformCollectionViewField field={field} variant={variant} className={className} />;
    case 'TagField':
      return <TagCollectionViewField field={field} variant={variant} className={className} />;
    case 'TextField':
      return <TextCollectionViewField field={field} variant={variant} className={className} />;
    case 'TodoField':
      return <TodoCollectionViewField field={field} variant={variant} className={className} />;
    case 'UrlField':
      return <UrlCollectionViewField field={field} variant={variant} className={className} />;
    default:
      return <UnknownCollectionViewField field={field} />;
  }
});
